import { useState, useEffect, useCallback } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { Shimmer, TableShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/data-table-pagination";
import TransactionFiltersPanel, {
  getDefaultFilters,
  type FilterValues,
} from "@/components/common/TransactionFiltersPanel";
import { useAuth } from "@/context/AuthContext";
import {
  getOrderHistory,
  getAllOrders,
  getOrderStatuses,
  getOrderServiceName,
  getOrderServiceImage,
  getOrderPlanName,
  type OrderRow,
  type OrderFilters,
} from "@/utils/order-history-api";
import { exportOrdersToExcel } from "@/utils/export-excel";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const statusBadgeClass: Record<string, string> = {
  Done: "border-green-500 text-green-600 dark:text-green-400",
  Pending: "border-yellow-500 text-yellow-600 dark:text-yellow-400",
  Refunded: "border-red-500 text-red-500 dark:text-red-400",
  Failed: "border-red-500 text-red-500 dark:text-red-400",
};

export default function GiftCardsOrdersPage() {
  const { userId, token } = useAuth();

  const [rows, setRows] = useState<OrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<FilterValues>(getDefaultFilters);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);

  // Fetch statuses
  useEffect(() => {
    if (!userId || !token) return;
    getOrderStatuses(userId, token).then(setAvailableStatuses).catch(() => {});
  }, [userId, token]);

  const buildFilters = useCallback((): OrderFilters => {
    const f: OrderFilters = { userId: userId! };
    if (filters.selectedTypes.length > 0) f.statuses = filters.selectedTypes;
    if (filters.createdFrom) f.createdFrom = filters.createdFrom.startOf("day").toISOString();
    if (filters.createdTo) f.createdTo = filters.createdTo.endOf("day").toISOString();
    if (filters.search.trim()) f.search = filters.search.trim();
    return f;
  }, [userId, filters]);

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    setLoading(true);
    setError("");
    try {
      const result = await getOrderHistory(buildFilters(), rowsPerPage, page * rowsPerPage, token);
      setRows(result.rows);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [userId, token, page, rowsPerPage, buildFilters]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(0); }, [filters]);

  const handleExport = async () => {
    if (!userId || !token) return;
    setExporting(true);
    try {
      const allRows = await getAllOrders(buildFilters(), token);
      exportOrdersToExcel(allRows, "gift-card-orders");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyText = (text: string) => { navigator.clipboard.writeText(text); };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold">
          Gift Cards &mdash; Order History
        </h1>
        <p className="text-muted-foreground">
          View all gift card orders, coupons, and delivery statuses.
        </p>
      </div>

      <Shimmer loading={loading} fallback={<TableShimmer cols={8} />}>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold">Order History</h3>
                <span className="text-sm text-muted-foreground">
                  {total.toLocaleString()} orders total
                </span>
              </div>
            </div>

            <TransactionFiltersPanel
              filters={filters}
              onChange={setFilters}
              availableTypes={availableStatuses}
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={exporting || total === 0}
                >
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {exporting ? "Exporting..." : "Export Excel"}
                </Button>
              }
            />

            {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Service</TableHead>
                  <TableHead className="font-semibold">Plan</TableHead>
                  <TableHead className="font-semibold">Coupon</TableHead>
                  <TableHead className="font-semibold text-right">Face Value</TableHead>
                  <TableHead className="font-semibold text-right">Paid</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Allocated</TableHead>
                  <TableHead className="font-semibold">Expires</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <span className="text-muted-foreground">No orders found</span>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7 rounded-md">
                            <AvatarImage src={getOrderServiceImage(order)} />
                            <AvatarFallback className="rounded-md">{getOrderServiceName(order)[0]}</AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium max-w-[140px] truncate" title={getOrderServiceName(order)}>
                            {getOrderServiceName(order)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground max-w-[140px] truncate block" title={getOrderPlanName(order)}>
                          {getOrderPlanName(order)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger>
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-mono">
                                {order.coupon.slice(0, 10)}{order.coupon.length > 10 ? "..." : ""}
                              </span>
                              <Button variant="ghost" size="icon-xs" onClick={() => copyText(order.coupon)}>
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {`Coupon: ${order.coupon}${order.pin ? ` | PIN: ${order.pin}` : ""}`}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm">₹{order.amount.toLocaleString("en-IN")}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-sm font-semibold text-primary">
                          ₹{order.discounted_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={statusBadgeClass[order.delivery_status] ?? ""}
                        >
                          {order.delivery_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {order.allocated_at ? formatDate(order.allocated_at) : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {order.expiring_at ?? "—"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <DataTablePagination
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(p) => setPage(p)}
              onRowsPerPageChange={(rpp) => { setRowsPerPage(rpp); setPage(0); }}
            />
          </CardContent>
        </Card>
      </Shimmer>
    </div>
  );
}
